import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { templateApi } from '../services/data_apis';
import { ArrowLeft, Save, Plus, Trash2, Edit2, Check, X } from 'lucide-react';

interface TemplateRow {
  id: string;
  topic: string;
  question: string;
  sources: string;
}

export const CreateBlankTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [rows, setRows] = useState<TemplateRow[]>([
    { id: '1', topic: '', question: '', sources: '' }
  ]);
  const [editingCell, setEditingCell] = useState<{rowId: string, field: keyof TemplateRow} | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addRow = () => {
    const newRow: TemplateRow = {
      id: Date.now().toString(),
      topic: '',
      question: '',
      sources: ''
    };
    setRows([...rows, newRow]);
  };

  const deleteRow = (rowId: string) => {
    if (rows.length > 1) {
      setRows(rows.filter(row => row.id !== rowId));
    }
  };

  const updateRow = (rowId: string, field: keyof TemplateRow, value: string) => {
    setRows(rows.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  const startEditing = (rowId: string, field: keyof TemplateRow) => {
    setEditingCell({ rowId, field });
  };

  const stopEditing = () => {
    setEditingCell(null);
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }

    const filledRows = rows.filter(row => 
      row.topic.trim() || row.question.trim() || row.sources.trim()
    );

    if (filledRows.length === 0) {
      setError('Please add at least one row with content');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Get Auth0 token for authentication
      let authToken: string | undefined;
      if (isAuthenticated) {
        try {
          authToken = await getAccessTokenSilently();
        } catch (authErr) {
          console.error('Failed to get auth token:', authErr);
          setError('Authentication failed. Please log in again.');
          return;
        }
      }

      // Group rows by topic to create the structure expected by the backend
      const topicsMap = new Map<string, { questions: Array<{text: string, sources: string[]}> }>();
      
      filledRows.forEach((row, index) => {
        const topicName = row.topic.trim() || `Topic ${index + 1}`;
        if (!topicsMap.has(topicName)) {
          topicsMap.set(topicName, { questions: [] });
        }
        
        if (row.question.trim()) {
          topicsMap.get(topicName)!.questions.push({
            text: row.question.trim(),
            sources: row.sources.split(',').map(s => s.trim()).filter(s => s.length > 0)
          });
        }
      });

      // Convert to the format expected by the backend
      const topics = Array.from(topicsMap.entries()).map(([topicName, data], topicIdx) => ({
        id: `topic${topicIdx + 1}`,
        title: topicName,
        sources: {},
        questions: data.questions.map((q, idx) => ({
          id: `question${idx + 1}`,
          text: q.text,
          sources: q.sources
        }))
      }));

      const response = await templateApi.saveTemplate({
        name: templateName.trim(),
        description: templateDescription.trim(),
        query: templateName.trim(), // Use template name as query for manual templates
        research_length: 'manual', // Special indicator for manual templates
        topics,
        is_public: false
      }, authToken);

      if (response.success) {
        navigate('/templates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/home')}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={saving || !templateName.trim()}
            className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Template
              </>
            )}
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}


        {/* Editable Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter template name..."
                  className="text-lg font-semibold w-80"
                />
              </div>
              <Button
                onClick={addRow}
                size="sm"
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Row
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <th className="text-left p-4 font-semibold text-gray-900 dark:text-gray-100 w-1/4">Topic</th>
                    <th className="text-left p-4 font-semibold text-gray-900 dark:text-gray-100 w-1/2">Question</th>
                    <th className="text-left p-4 font-semibold text-gray-900 dark:text-gray-100 w-1/4">Sources</th>
                    <th className="text-left p-4 font-semibold text-gray-900 dark:text-gray-100 w-12">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr 
                      key={row.id}
                      className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      {/* Topic Cell */}
                      <td className="p-4">
                        {editingCell?.rowId === row.id && editingCell?.field === 'topic' ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={row.topic}
                              onChange={(e) => updateRow(row.id, 'topic', e.target.value)}
                              onBlur={stopEditing}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') stopEditing();
                                if (e.key === 'Escape') stopEditing();
                              }}
                              autoFocus
                              className="text-sm"
                            />
                          </div>
                        ) : (
                          <div 
                            onClick={() => startEditing(row.id, 'topic')}
                            className="min-h-[2rem] flex items-center cursor-text hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-2 py-1"
                          >
                            {row.topic || (
                              <span className="text-gray-400 italic">Click to add topic...</span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Question Cell */}
                      <td className="p-4">
                        {editingCell?.rowId === row.id && editingCell?.field === 'question' ? (
                          <Textarea
                            value={row.question}
                            onChange={(e) => updateRow(row.id, 'question', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                stopEditing();
                              }
                              if (e.key === 'Escape') stopEditing();
                            }}
                            autoFocus
                            className="text-sm min-h-[2rem] resize-none"
                            rows={2}
                          />
                        ) : (
                          <div 
                            onClick={() => startEditing(row.id, 'question')}
                            className="min-h-[2rem] flex items-center cursor-text hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-2 py-1"
                          >
                            {row.question || (
                              <span className="text-gray-400 italic">Click to add question...</span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Sources Cell */}
                      <td className="p-4">
                        {editingCell?.rowId === row.id && editingCell?.field === 'sources' ? (
                          <Input
                            value={row.sources}
                            onChange={(e) => updateRow(row.id, 'sources', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') stopEditing();
                              if (e.key === 'Escape') stopEditing();
                            }}
                            autoFocus
                            placeholder="Separate multiple sources with commas"
                            className="text-sm"
                          />
                        ) : (
                          <div 
                            onClick={() => startEditing(row.id, 'sources')}
                            className="min-h-[2rem] flex items-center cursor-text hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-2 py-1"
                          >
                            {row.sources || (
                              <span className="text-gray-400 italic">Click to add sources...</span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Actions Cell */}
                      <td className="p-4">
                        <Button
                          onClick={() => deleteRow(row.id)}
                          disabled={rows.length === 1}
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-30"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Instructions */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Instructions:</strong> Click on any cell to edit. For sources, separate multiple sources with commas (e.g., "Bloomberg, Reuters, Goldman Sachs").
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};